// ./src/components/features/Rentals/RentalsFilterBar/RentalsFilterBar.tsx
//
// Filter controls for the event Rentals list: by rental lifecycle status (reserved
// / in possession / returned / canceled) and by category. Fully controlled — the
// page owns the value and maps it to an EventRentalFilter.

import React from "react";
import { Group, Button, Box, Stack } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { RENTAL_STATUS_VALUES, RENTAL_STATUS_LABELS } from "@/utils/api";
import { AppIcon, CategorySelect, SelectField } from "@/components/generic";

export interface RentalsFilterValue {
  rentalStatus: string | null;
  categoryId: string | null;
}

export interface RentalsFilterBarProps {
  value: RentalsFilterValue;
  onChange: (value: RentalsFilterValue) => void;
  disabled?: boolean;
  showClear?: boolean;
  vertical?: boolean;
}

export const EMPTY_RENTALS_FILTER: RentalsFilterValue = {
  rentalStatus: null,
  categoryId: null,
};

const isFilterActive = (value: RentalsFilterValue): boolean =>
  Boolean(value.rentalStatus) || Boolean(value.categoryId);

const FIELD_STYLE: React.CSSProperties = { flex: "1 1 200px", minWidth: 190 };

const RentalsFilterBarComponent: React.FC<RentalsFilterBarProps> = ({
  value,
  onChange,
  disabled = false,
  showClear = true,
  vertical = false,
}) => {
  const { t } = useTranslation(["rentals", "common"]);

  const rentalStatusData = RENTAL_STATUS_VALUES.map((v) => ({ value: v, label: RENTAL_STATUS_LABELS[v] || v }));

  const active = isFilterActive(value);

  const rentalStatusControl = (
    <Box style={vertical ? undefined : FIELD_STYLE}>
      <SelectField
        label={t("rentals:filters.rentalStatus.label", { defaultValue: "Rental status" })}
        placeholder={t("rentals:filters.rentalStatus.placeholder", { defaultValue: "Any" })}
        data={rentalStatusData}
        value={value.rentalStatus}
        onChange={(_n, v) => onChange({ ...value, rentalStatus: v })}
        clearable
        disabled={disabled}
        size="md"
      />
    </Box>
  );

  const categoryControl = (
    <Box style={vertical ? undefined : FIELD_STYLE}>
      <CategorySelect
        group="rental"
        label={t("rentals:filters.category.label", { defaultValue: "Category" })}
        placeholder={t("rentals:filters.category.placeholder", { defaultValue: "All categories" })}
        value={value.categoryId}
        onChange={(id) => onChange({ ...value, categoryId: id })}
        clearable
        disabled={disabled}
        size="md"
      />
    </Box>
  );

  if (vertical) {
    return (
      <Stack gap="md">
        {rentalStatusControl}
        {categoryControl}
      </Stack>
    );
  }

  return (
    <Group gap="sm" align="flex-end" wrap="wrap">
      {rentalStatusControl}
      {categoryControl}

      {active && showClear && (
        <Button
          variant="subtle"
          color="gray"
          size="md"
          onClick={() => onChange(EMPTY_RENTALS_FILTER)}
          disabled={disabled}
          leftSection={<AppIcon iconName="X" size={16} />}
        >
          {t("common:buttons.clear", { defaultValue: "Clear" })}
        </Button>
      )}
    </Group>
  );
};

export const RentalsFilterBar = React.memo(RentalsFilterBarComponent);
